import React, { useContext, useEffect, useState, createContext } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useState(null); // Firestore data (name, roles, etc.)
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch user data from Firestore
  const fetchUserData = async (firebaseUser) => {
    try {
      const ref = doc(db, "users", firebaseUser.uid);
      const snapshot = await getDoc(ref);

      if (snapshot.exists()) {
        const data = snapshot.data();
        const fullUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          ...data,
        };
        setUser(fullUser);
        return data;
      } else {
        const defaultUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
        };
        setUser(defaultUser);
        return {};
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      return {};
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setCurrentUser(firebaseUser);
      if (firebaseUser) {
        const data = await fetchUserData(firebaseUser);

        if (data?.isDisabled) {
          navigate("/disabled");
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [navigate]);

  // Watch for status changes and redirect if necessary
  useEffect(() => {
    if (user?.isDisabled) {
      navigate("/disabled");
    }
  }, [user?.isDisabled, navigate]);

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    currentUser,
    user,
    login,
    signup,
    logout,
    isAdmin: user?.isAdmin || false,
    isDisabled: user?.isDisabled || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
